import type { DataSyncTask } from './validations/types';

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApiNoConversion } from '~/services/kyClient';

import { queryKeysDataSyncJob } from './queryKeys';
import {
  parseDataSyncResponse,
  rawEntityResponseSchema,
} from './responseShapes';
import { fromApiKeepingUserKeys } from './userKeyedFields';

/**
 * Fetches a single data sync task by ID.
 *
 * @param taskId - The UUID of the task to fetch
 * @returns Promise containing the task data
 */
export const fetchDataSyncTask = async (
  taskId: UUID,
): Promise<DataSyncTask> => {
  const rawResponse = await vestigasApiNoConversion
    .get(ENDPOINT.DATA_SYNC.GET_TASK(taskId))
    .json<unknown>();

  return fromApiKeepingUserKeys(
    parseDataSyncResponse(rawEntityResponseSchema, rawResponse, 'task'),
  );
};

/**
 * React Query hook for fetching a single data sync task.
 *
 * @param taskId - The UUID of the task to fetch
 * @param options - Additional React Query options
 * @returns Query result containing the task data
 */
export const useQueryDataSyncTask = (
  taskId: UUID,
  options?: Omit<UseQueryOptions<DataSyncTask>, 'queryKey' | 'queryFn'>,
) => {
  const queryKey = queryKeysDataSyncJob.getTaskById(taskId);

  return useQuery({
    queryFn: async () => await fetchDataSyncTask(taskId),
    queryKey,
    staleTime: ms('5m'), // Cache for 5 minutes
    ...options,
  });
};
